import { useEffect, useState, useContext } from 'react'
import T from 'prop-types'
import { useImmer } from 'use-immer'
import { getCurrentUserId } from '../../api/auth-service'
import { useBookSeat } from '../../common/hooks'
import OfficeContext from './OfficeContext'
import { RejectedMain } from '../../common/components'
import ScaleToFit from '../../common/components/ScaleToFit'
import { Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import SeatMap from './SeatMap'
import BookingConfirmation from './BookingConfirmation'

let
  updateSeatList = (totalBooked, seatMap) => draft => {
    draft.seatMap = seatMap
    draft.totalBooked = totalBooked
  },
  setIsReserved = (rowId, seatNumber) => draft => {
    const
      row = draft.seatMap.find(row => row.id === rowId),
      seat = row?.seats.find(seat => seat.seatNumber === seatNumber)
    if (seat) seat.isReserved = true
  }

const handleAddSelectSeat = (isoDate, bookingLimit, totalBooked, setSeatList, addSeatBook, onSuccessOpr, seatList, selectedSeats, setSelectedSeats, isOps, showConfirmation, officeName) => (rowId, seatId) => {
  if (isOps) {
    let chosenSeats = selectedSeats;
    if (!chosenSeats.includes(seatId)) {
      chosenSeats.push(seatId);
    } else {
      chosenSeats.splice(chosenSeats.indexOf(seatId), 1);  //deleting
    }
    setSelectedSeats(chosenSeats);
  }
  else {
    if (totalBooked >= bookingLimit) {
      toast.warning(`Max number of reservations per user reached (limit: ${bookingLimit})`)
    } else {
      // Show confirmation modal instead of direct booking
      showConfirmation({
        seatId,
        rowId,
        date: isoDate,
        officeName
      })
    }
  }
}

const handleRemoveSelectSeat = (isoDate, removeSeatBook, onSuccessOpr, showCancelConfirmation, officeName) => (rowId, seatNumber) => {
  // Show confirmation modal instead of direct cancellation
  showCancelConfirmation({
    seatId: seatNumber,
    rowId,
    date: isoDate,
    officeName
  })
}
function SeatMapContainer({
  data: { isoDate, bookingLimit, totalBooked, seatMap, status, error },
  onSuccessOpr,
  isOps,
  selectedSeats,
  setSelectedSeats,
  officeName,
  isGroupBooking = false
}) {
  const { officeName: contextOfficeName } = useContext(OfficeContext)
  const userId = getCurrentUserId()
  const
    [, addSeatBook, removeSeatBook] = useBookSeat(),
    [seatList, setSeatList] = useImmer({}),
    [confirmationModal, setConfirmationModal] = useState({ show: false, seatInfo: null, isLoading: false, actionType: 'book' })

  useEffect(() => seatMap && setSeatList({ totalBooked, seatMap }),
    [totalBooked, seatMap, setSeatList],
  )

  const showConfirmation = (seatInfo) => {
    setConfirmationModal({ show: true, seatInfo, isLoading: false, actionType: 'book' })
  }

  const showCancelConfirmation = (seatInfo) => {
    setConfirmationModal({ show: true, seatInfo, isLoading: false, actionType: 'cancel' })
  }

  const hideConfirmation = () => {
    setConfirmationModal({ show: false, seatInfo: null, isLoading: false, actionType: 'book' })
  }

  const handleConfirmAction = () => {
    const { seatId, rowId } = confirmationModal.seatInfo
    const { actionType } = confirmationModal
    setConfirmationModal(prev => ({ ...prev, isLoading: true }))

    if (actionType === 'book') {
      addSeatBook(isoDate, seatId, userId)
        .then(({ totalBooked, seatMap }) => {
          setSeatList(updateSeatList(totalBooked, seatMap))
          onSuccessOpr()
          hideConfirmation()
          toast.success(`Seat '${seatId}' in Row '${rowId}' reserved successfully!`)
        })
        .catch(error => {
          console.error('handleConfirmBooking', error)
          if (error.response?.status === 403) setSeatList(setIsReserved(rowId, seatId))
          hideConfirmation()
          toast.error(error.response?.status === 403 ? 'Seat has already been booked!' : 'Booking request failed!')
        })
    } else if (actionType === 'cancel') {
      removeSeatBook(isoDate, seatId, userId)
        .then(({ totalBooked, seatMap }) => {
          setSeatList(updateSeatList(totalBooked, seatMap))
          onSuccessOpr()
          hideConfirmation()
          toast.success(`Seat '${seatId}' in Row '${rowId}' unreserved successfully!`)
        })
        .catch(error => {
          console.error('handleConfirmCancellation', error)
          hideConfirmation()
          toast.error('Cancellation request failed!')
        })
    }
  }

  const
    addSelectSeat = handleAddSelectSeat(isoDate, bookingLimit, seatList.totalBooked, setSeatList, addSeatBook, onSuccessOpr, seatList, selectedSeats, setSelectedSeats, isOps, showConfirmation, officeName || contextOfficeName),
    removeSelectSeat = handleRemoveSelectSeat(isoDate, removeSeatBook, onSuccessOpr, showCancelConfirmation, officeName || contextOfficeName)

  if (status.isLoading)
    return (
      <div className="flex h-[480px] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )

  if (status.isRejected)
    return (
      <RejectedMain>
        <p>Error on fetching the seat map: {error.response?.data ?? error.message}</p>
      </RejectedMain>
    )
  if (status.isResolved && seatList.seatMap?.length)
    return (
      <div className="pt-4 w-full min-w-0">
        <ScaleToFit>
          <SeatMap
            data={seatList.seatMap}
            onAddSeat={addSelectSeat}
            onRemoveSeat={removeSelectSeat}
            isOps={isOps}
            officeName={officeName || contextOfficeName}
            isGroupBooking={isGroupBooking}
          />
        </ScaleToFit>
        <BookingConfirmation
          show={confirmationModal.show}
          onCancel={hideConfirmation}
          onConfirm={handleConfirmAction}
          seatInfo={confirmationModal.seatInfo}
          isLoading={confirmationModal.isLoading}
          actionType={confirmationModal.actionType}
        />
      </div>
    )
  return null
}
SeatMapContainer.propTypes = {
  data: T.object.isRequired,
}
export default SeatMapContainer
